import type { Theme } from '@emotion/react'
import { css } from '@emotion/react'
import {
  breakpoint,
  duration,
  pxToRem,
  size,
  width,
} from '@twistopayments/design-system/styles'

export const navigation = (theme: Theme) => css`
  position: sticky;
  top: 68px;
  padding: 0 ${size[20]} ${size[8]};
  display: flex;
  flex-direction: column-reverse;
  background-color: ${theme.palette.background.main};
  box-shadow: 0px 15px 15px 0px rgba(130, 139, 153, 0.2);
  z-index: 11;

  ${breakpoint.up('smMd')} {
    padding-bottom: ${size[20]};
    flex-direction: row;
    align-items: center;
    top: 96px;
  }
`

export const navContainer = css`
  display: flex;
  flex-direction: column-reverse;

  ${breakpoint.up('smMd')} {
    margin: 0 auto;
    padding: 0 ${size[18]};
    max-width: ${width.container.wider};
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`

export const navList = css`
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: nowrap;
  gap: 0;
  list-style: none;
`

export const horizontalList = css`
  overflow-x: auto;
  align-items: center;

  & > div {
    display: flex;
  }

  ${breakpoint.up('smMd')} {
    overflow-x: visible;
  }
`

export const listItem = (theme: Theme) => css`
  padding: ${size[16]} ${size[12]};
  width: auto;
  flex: 0 0 auto;
  background-color: transparent;
  border: none;
  color: ${theme.palette.text.title};
  font-size: ${pxToRem(19)};
  font-weight: 500;
  text-transform: capitalize;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: ${theme.palette.primary.mainContrast};
  }
`

export const activeItem = (theme: Theme) => css`
  color: ${theme.palette.primary.mainContrast};
`

export const dropdownLabel = css`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${size[4]};
  text-transform: none;

  svg {
    margin-top: 2px;
  }

  &:hover > ul {
    opacity: 1;
    pointer-events: all;
    transform: translateY(0);
  }
`

export const dropdownList = (theme: Theme) => css`
  position: absolute;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${size[12]};
  column-gap: ${size[32]};
  top: 100%;
  left: -16px;
  opacity: 0;
  pointer-events: none;
  transform: translateY(-10px);
  transition:
    opacity 0.2s ease-out,
    transform 0.2s ease-out;
  z-index: 12;

  background-color: ${theme.palette.background.main};
  border-radius: ${size[8]} ${size[32]};
  box-shadow: 0 10px 40px rgba(130, 139, 153, 0.4);
  padding: ${size[54]};
  min-width: 400px;
  width: fit-content;
  max-width: calc(100vw + 32px);
  overflow: hidden;

  li {
    padding: 0;
  }

  &::before {
    content: '';
    height: 6px;
    background-color: ${theme.palette.primary.mainContrast};
    width: 140px;
    display: block;
    position: absolute;
    border-radius: 3px;
    top: -3px;
    left: 30px;
  }
`

export const searchWrap = css`
  position: relative;

  svg {
    position: absolute;
    top: 50%;
    left: ${size[12]};
    transform: translateY(-50%);
  }
`

export const searchBar = (theme: Theme) => css`
  padding: ${size[12]};
  padding-left: ${size[40]};
  width: 100%;
  background-color: ${theme.palette.background.accent};
  color: ${theme.palette.text.title};
  border: none;
  border-radius: ${theme.borderRadius.sm};
  font-size: ${pxToRem(19)};
  font-weight: 400;
  transition: background-color ${duration.standard}ms ease-in-out;

  &:focus {
    background-color: ${theme.palette.background.accent};
    transition: background-color ${duration.standard}ms ease-in-out;
  }

  ${breakpoint.up('smMd')} {
    width: 320px;
  }
`
